const AUTH_REQUIRED_MESSAGE = 'User must be authenticated';

/**
 * Extract error message from unknown error type
 * @param value - Error value of unknown type
 * @param defaultValue - Default message if extraction fails (default: 'Unknown error')
 * @returns Extracted error message or default value
 * @remarks This is for internal use. Use getUserFriendlyMessage from error-handler.ts for user-facing messages.
 */
export function getErrorMessage(value: unknown, defaultValue = 'Unknown error'): string {
  if (value instanceof Error) {
    return value.message || defaultValue;
  }

  if (typeof value === 'string') {
    return value || defaultValue;
  }

  if (value && typeof value === 'object') {
    const maybeMessage = (value as { message?: unknown }).message;
    if (typeof maybeMessage === 'string') {
      return maybeMessage || defaultValue;
    }
  }

  return defaultValue;
}

// getUserFriendlyMessage has been moved to @/lib/error-handler
// Import directly from there to avoid circular dependencies

export const isAuthenticationRequiredSyncError = (error: unknown): boolean => {
  const message = getErrorMessage(error, '');
  if (message.includes(AUTH_REQUIRED_MESSAGE)) {
    return true;
  }

  if (error && typeof error === 'object' && 'originalError' in error) {
    const originalError = (error as { originalError?: unknown }).originalError;
    const originalMessage = getErrorMessage(originalError, '');
    if (originalMessage.includes(AUTH_REQUIRED_MESSAGE)) {
      return true;
    }
  }

  return false;
};

/**
 * Check if error indicates a blocked request (e.g., content blocker)
 * @param error - Error to check
 * @returns True if error indicates a blocked request
 */
export function isBlockedError(error: unknown): boolean {
  const message = getErrorMessage(error);
  return message.includes('blocked') || message.includes('ERR_BLOCKED');
}
